import Intercom, { shutdown as shutdownIntercom, update as updateIntercom } from '@intercom/messenger-js-sdk'
import { useAuth } from 'hooks/auth'
import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { GENERAL_AGENT_PAGE_PATH } from '@/features/general-agent/constants'
import { useGeneralAgentChatStore } from '@/features/general-agent/hooks/generalAgentChatStore'
import { useAppSelector } from '@/hooks'
import { selectFeatureFlags } from '@/store/authSlice'

const INTERCOM_APP_ID = process.env.REACT_APP_INTERCOM_APP_ID

const DISABLED_ROUTES = ['/dispatcher/ai-hub', '/dispatcher/load-hub', '/dispatcher/dispatch-help', '/dispatcher/box-truck-', '/fuel-ai']

interface UseIntercomOptions {
    enabledRoutes?: string[]
}

/**
 * Hook to manage Intercom messenger visibility based on routes
 * @param options.enabledRoutes - Array of route prefixes where Intercom should be shown (default: ['/dispatcher'])
 */
export function useIntercom(options: UseIntercomOptions = {}) {
    const { enabledRoutes = ['/dispatcher'] } = options
    const { user } = useAuth()
    const location = useLocation()
    const generalAgentEnabled = useAppSelector(selectFeatureFlags).generalAgent ?? false
    const panelOpen = useGeneralAgentChatStore((state) => state.panelOpen)
    const hideLauncher = generalAgentEnabled && panelOpen && !location.pathname.startsWith(GENERAL_AGENT_PAGE_PATH)

    const isOnEnabledRoute = enabledRoutes.some((route) => location.pathname.startsWith(route))
    const isOnDisabledRoute = DISABLED_ROUTES.some((route) => location.pathname.startsWith(route))

    useEffect(() => {
        if (!isOnEnabledRoute || isOnDisabledRoute) {
            shutdownIntercom()
            return
        }

        if (!INTERCOM_APP_ID) {
            return
        }

        Intercom({
            app_id: INTERCOM_APP_ID,
            name: user?.name,
            email: user?.email,
            user_id: user?.id,
            horizontal_padding: 60,
            vertical_padding: 80,
            // Start hidden; the route-aware effect below owns launcher visibility.
            hide_default_launcher: true,
        })

        return () => {
            shutdownIntercom()
        }
    }, [isOnEnabledRoute, isOnDisabledRoute, user?.name, user?.email, user?.id])

    useEffect(() => {
        if (!INTERCOM_APP_ID || !isOnEnabledRoute || isOnDisabledRoute) return

        updateIntercom({ hide_default_launcher: hideLauncher })
    }, [isOnEnabledRoute, isOnDisabledRoute, hideLauncher, location.pathname, user?.name, user?.email, user?.id])
}
